import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup } from 'semantic-ui-react';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { LoaderBar, SkupremeModal } from '../../components';
import {
  CenterDropdownStyle,
  DangerText,
  HoverBorderButton,
  MuteText,
  NumberFormField,
  StyledButton,
} from '../../elements';
import { InventoryService, ToastMessage } from '../../utility';
import { MetricImperialToggle } from '../../components/MetricImperialToggle';
import { ConversionUtility } from '../../utility/conversions';
import { useLotAttributesHook } from '../../hooks';

const ItemSchema = yup.object().shape({
  cartonLength: yup
    .number()
    .positive('Length is required')
    .typeError('Length is required'),
  cartonWidth: yup.number().positive('Width is required').typeError('Width is required'),
  cartonHeight: yup
    .number()
    .positive('Height is required')
    .typeError('Height is required'),
  cartonGrossWeight: yup
    .number()
    .positive('Weight is required')
    .typeError('Weight is required'),
  cartonNetWeight: yup
    .number()
    .positive('Weight is required')
    .typeError('Weight is required'),
  unitsPerCarton: yup
    .number()
    .integer('Units per carton must be an integer')
    .required('Units per carton is required')
    .typeError('Units per carton must be an integer'),
});

const decimalMask = {
  allowDecimal: true,
  allowLeadingZeroes: false,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

const UnitLabel = styled.div`
  border-left: none !important;
  &.error {
    border-color: #e0b4b4 !important;
    color: #e7bdbc !important;
    background: #fff6f6 !important;
  }
  font-weight: 400 !important;
  color: ${({ theme }) => theme.colors.grey} !important;
`;

const attributeSchema = {
  cartonHeight: '',
  cartonLength: '',
  cartonWidth: '',
  cartonGrossWeight: '',
  cartonNetWeight: '',
};

export function EditLotAttributePopup({ item, onClose, refresh, open }) {
  const [loading, setLoading] = useState(false);
  const [ImperialForm, setImperialForm] = useState(true);
  const [aliasName, setAliasName] = useState('');
  const [aliasOptions, setAliasOptions] = useState([]);
  const [aliasError, setAliasError] = useState('');

  const { data, loading: lotAttributesLoading } = useLotAttributesHook(item?.itemId);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  useEffect(() => {
    setAliasName(item?.cartonAttributes?.alias || '');
  }, [item]);

  useEffect(() => {
    const options =
      data?.cartonAttributes?.map(attribute => ({
        text: attribute.alias,
        value: attribute.alias,
        key: attribute.alias,
        item: attribute,
      })) || [];

    if (!options.find(x => x.value === item?.cartonAttributes?.alias)) {
      options.push({
        text: item?.cartonAttributes?.alias,
        value: item?.cartonAttributes?.alias,
        key: item?.cartonAttributes?.alias,
      });
    }

    setAliasOptions(options);
  }, [data, item]);

  useEffect(() => {
    const aliasToUse = data?.cartonAttributes?.find(attr => attr.alias === aliasName);
    resetItemUsingAlias(aliasToUse);
  }, [aliasName, data]);

  const resetItemUsingAlias = aliasToUse => {
    let itemToReset = {
      cartonLength: aliasToUse?.cartonLength?.value,
      cartonHeight: aliasToUse?.cartonHeight?.value,
      cartonWidth: aliasToUse?.cartonWidth?.value,
      cartonNetWeight: aliasToUse?.cartonNetWeight?.value,
      cartonGrossWeight: aliasToUse?.cartonGrossWeight?.value,
      alias: aliasToUse?.alias,
      unitsPerCarton: aliasToUse?.unitsPerCarton,
    };

    const isOriginallyMetric = aliasToUse?.cartonLength?.unit === 'cm';

    if (isOriginallyMetric && ImperialForm) {
      const convertedFields = convertFieldsToImperial(itemToReset);
      itemToReset = {
        ...itemToReset,
        ...convertedFields,
      };
    }

    reset(itemToReset);
  };

  const changeUnits = () => {
    const watchFields = getValues();
    let newItem = null;

    if (ImperialForm) {
      newItem = convertFieldsToMetric(watchFields);
    } else {
      newItem = convertFieldsToImperial(watchFields);
    }

    reset({
      ...watchFields,
      ...newItem,
    });

    setImperialForm(!ImperialForm);
  };

  const convertFieldsToMetric = watchFields => {
    const item = attributeSchema;

    Object.keys(attributeSchema).forEach(key => {
      if (['cartonNetWeight', 'cartonGrossWeight'].includes(key)) {
        item[key] = ConversionUtility.poundToKilo(watchFields[key]);
      } else {
        item[key] = ConversionUtility.inchToCenti(watchFields[key]);
      }
    });

    return item;
  };

  const convertFieldsToImperial = watchFields => {
    const item = attributeSchema;

    Object.keys(attributeSchema).forEach(key => {
      if (['cartonNetWeight', 'cartonGrossWeight'].includes(key)) {
        item[key] = ConversionUtility.kiloToPound(watchFields[key]);
      } else {
        item[key] = ConversionUtility.centiToInch(watchFields[key]);
      }
    });

    return item;
  };

  const onSubmit = async formValues => {
    if (!aliasName) {
      setAliasError('Alias is required');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        cartonAttributes: {
          alias: aliasName,
          unitsPerCarton: formValues.unitsPerCarton,
          cartonLength: {
            value: formValues.cartonLength,
            unit: 'cm',
          },
          cartonWidth: {
            value: formValues.cartonWidth,
            unit: 'cm',
          },
          cartonHeight: {
            value: formValues.cartonHeight,
            unit: 'cm',
          },
          cartonGrossWeight: {
            value: formValues.cartonGrossWeight,
            unit: 'kg',
          },
          cartonNetWeight: {
            value: formValues.cartonNetWeight,
            unit: 'kg',
          },
        },
      };

      if (ImperialForm) {
        payload.cartonAttributes.cartonLength.value = ConversionUtility.inchToCenti(
          payload.cartonAttributes.cartonLength.value,
        );
        payload.cartonAttributes.cartonWidth.value = ConversionUtility.inchToCenti(
          payload.cartonAttributes.cartonWidth.value,
        );
        payload.cartonAttributes.cartonHeight.value = ConversionUtility.inchToCenti(
          payload.cartonAttributes.cartonHeight.value,
        );
        payload.cartonAttributes.cartonGrossWeight.value = ConversionUtility.poundToKilo(
          payload.cartonAttributes.cartonGrossWeight.value,
        );
        payload.cartonAttributes.cartonNetWeight.value = ConversionUtility.poundToKilo(
          payload.cartonAttributes.cartonNetWeight.value,
        );
      }

      await InventoryService.updateLotAttribute(item.itemId, item.inventoryId, payload);
      ToastMessage.success('Attribute updated successfully');
      onClose();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const onAddNewAlias = (e, { value: newAliasValue }) => {
    setAliasOptions(options => [
      ...options,
      { text: newAliasValue, value: newAliasValue, key: newAliasValue },
    ]);
  };

  const onChangeAlias = (e, data) => {
    setAliasName(data.value);
    setAliasError('');
  };

  return (
    <SkupremeModal
      size="tiny"
      onClose={onClose}
      open={open}
      title={`${item?.cartonAttributes?.alias ? 'Edit' : 'Create'} Carton Configuration`}
    >
      {lotAttributesLoading && <LoaderBar />}
      <MetricImperialToggle
        toggle
        onChange={() => changeUnits()}
        checked={ImperialForm}
        className="mx-3"
        wrapperClassName="mt-2 mb-3"
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <MuteText>Carton Configuration Name</MuteText>
          <CenterDropdownStyle
            className="mute-dropdown"
            options={aliasOptions}
            value={aliasName}
            onChange={onChangeAlias}
            selection
            search
            allowAdditions
            error={aliasError}
            placeholder="Type to add new alias"
            onAddItem={onAddNewAlias}
          />
          {aliasError && <DangerText>{aliasError}</DangerText>}
        </div>
        <FormGroup widths="equal">
          <NumberFormField
            control={control}
            name="unitsPerCarton"
            label="Quantity Per Carton"
            error={errors.unitsPerCarton}
            hint={errors.unitsPerCarton?.message}
            placeholder="Net"
          />
          <NumberFormField
            control={control}
            maskOptions={decimalMask}
            name="cartonGrossWeight"
            label="Gross Weight"
            error={errors.cartonGrossWeight}
            hint={errors.cartonGrossWeight?.message}
            placeholder="Net"
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel
                className={`ui basic label ${errors.cartonGrossWeight && 'error'}`}
              >
                {ImperialForm ? 'lb' : 'kg'}
              </UnitLabel>
            }
          />
          <NumberFormField
            control={control}
            maskOptions={decimalMask}
            name="cartonNetWeight"
            label="Net Weight"
            error={errors.cartonNetWeight}
            hint={errors.cartonNetWeight?.message}
            placeholder="Net"
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel
                className={`ui basic label ${errors.cartonNetWeight && 'error'}`}
              >
                {ImperialForm ? 'lb' : 'kg'}
              </UnitLabel>
            }
          />
        </FormGroup>
        <MuteText>Dimensions (Length x Width x Height)</MuteText>
        <FormGroup widths="equal">
          <NumberFormField
            control={control}
            name="cartonLength"
            error={errors.cartonLength}
            hint={errors.cartonLength?.message}
            maskOptions={decimalMask}
            placeholder="Length"
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.cartonLength && 'error'}`}>
                {ImperialForm ? 'in.' : 'cm'}
              </UnitLabel>
            }
          />
          <NumberFormField
            control={control}
            maskOptions={decimalMask}
            name="cartonWidth"
            placeholder="Width"
            error={errors.cartonWidth}
            hint={errors.cartonWidth?.message}
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.cartonWidth && 'error'}`}>
                {ImperialForm ? 'in.' : 'cm'}
              </UnitLabel>
            }
          />
          <NumberFormField
            maskOptions={decimalMask}
            placeholder="Height"
            control={control}
            name="cartonHeight"
            error={errors.cartonHeight}
            hint={errors.cartonHeight?.message}
            className="align-unset fluid ui right labeled input"
            inputRight={
              <UnitLabel className={`ui basic label ${errors.cartonHeight && 'error'}`}>
                {ImperialForm ? 'in.' : 'cm'}
              </UnitLabel>
            }
          />
        </FormGroup>
        <div className="d-flex justify-content-end mt-4">
          <HoverBorderButton onClick={onClose} disabled={loading} type="button">
            Cancel
          </HoverBorderButton>
          <StyledButton loading={loading} className="ml-1">
            Save Changes
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
